import { useEffect, useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import type { IntakeShellRenderProps } from './IntakeShell.js';

/**
 * wd-2.3.8 — Step 7 "Service catalog seed" for the prospect-facing
 * intake portal.
 *
 * Fields mirror `intakeStep7Schema` in `packages/config/src/intake-steps.ts`:
 *
 *   businessHours: { monday..sunday }, each:
 *     { isOpen, open?, close? } refined to require open+close when
 *     isOpen=true and to require close > open.
 *   holidays: Array<{ date (YYYY-MM-DD), label, closed }>
 *   services: Array<intakeServiceSeed> (1..200, unique slug)
 *     - slug, displayName, type, priceCents, durationMinutes (nullable),
 *       applicableStaffRoles[], minLeadTimeMinutes, maxAdvanceDays
 *     (SPEC 2026-10-26 AC 7.1: the "Requires vaccination check" box is gone
 *     — nothing ever read it, and a shop's vaccine rules now live in
 *     Settings → Operations → Required vaccinations once it is live. The
 *     wizard no longer sends the key; the schema's `.default(true)` keeps
 *     every older draft and tenant kit that carries it parsing unchanged.)
 *   capacityRules: { dailyDaycareCap?, overnightBoardingCap?,
 *                    concurrentGroomingSlots?, concurrentWalksPerWalker (default 3) }
 *   terminologyOverrides: Record<string,string>
 *
 * Pattern lifted from Step 5 (row editor) for `services` + `holidays`,
 * and from Step 2 (terminology editor) for `terminologyOverrides`.
 *
 * Mount defaults seed:
 *   - all 7 days closed (isOpen=false) so the prospect explicitly opens
 *     each day they operate;
 *   - one starter service row pre-filled with slug=`daycare`,
 *     displayName=`Daycare`, type=`daycare` so the schema's `min(1)`
 *     refine is trivially satisfied on a fresh load;
 *   - `capacityRules.concurrentWalksPerWalker = 3` to surface the
 *     schema default in the UI.
 */

const DAYS = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
] as const;
type DayKey = (typeof DAYS)[number]['key'];

const SERVICE_TYPES = [
  { value: 'daycare', label: 'Daycare' },
  { value: 'boarding', label: 'Boarding' },
  { value: 'grooming', label: 'Grooming' },
  { value: 'walking', label: 'Walking' },
  { value: 'training', label: 'Training' },
  { value: 'retail', label: 'Retail' },
  { value: 'other', label: 'Other' },
] as const;
type ServiceType = (typeof SERVICE_TYPES)[number]['value'];

const VALID_SERVICE_TYPES: ReadonlySet<ServiceType> = new Set(
  SERVICE_TYPES.map((s) => s.value),
);

// Mirrors INTAKE_ROLE_SLUGS in packages/config/src/intake-steps.ts.
const ROLE_OPTIONS = [
  { value: 'owner', label: 'Owner' },
  { value: 'manager', label: 'Manager' },
  { value: 'receptionist', label: 'Receptionist' },
  { value: 'walker', label: 'Walker' },
  { value: 'groomer', label: 'Groomer' },
] as const;
type RoleSlug = (typeof ROLE_OPTIONS)[number]['value'];
const VALID_ROLES: ReadonlySet<RoleSlug> = new Set(
  ROLE_OPTIONS.map((o) => o.value),
);

const HHMM_REGEX = /^\d{2}:\d{2}$/;
const SERVICE_SLUG_REGEX = /^[a-z0-9-]+$/;
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const MAX_SERVICE_ROWS = 200;
const MAX_TERMINOLOGY_ROWS = 50;

type DayHours = {
  isOpen: boolean;
  open: string;
  close: string;
};

type Holiday = {
  date: string;
  label: string;
  closed: boolean;
};

type ServiceRow = {
  slug: string;
  displayName: string;
  type: ServiceType | '';
  priceDollars: string; // cents-as-string in dollars; serialized to int cents
  durationMinutes: string; // empty string = nullable null
  applicableStaffRoles: RoleSlug[];
  minLeadTimeMinutes: string; // numeric string; defaults to '0'
  maxAdvanceDays: string; // numeric string; defaults to '90'
};

type CapacityDraft = {
  dailyDaycareCap: string;
  overnightBoardingCap: string;
  concurrentGroomingSlots: string;
  concurrentWalksPerWalker: string;
};

type Step7Draft = {
  businessHours: Record<DayKey, DayHours>;
  holidays: Holiday[];
  services: ServiceRow[];
  capacity: CapacityDraft;
  terminology: Array<[string, string]>;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringField(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function numericString(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'string' && value !== '') return value;
  return '';
}

function readDayHours(value: unknown): DayHours {
  const row = isRecord(value) ? value : {};
  return {
    isOpen: row.isOpen === true,
    open: stringField(row.open),
    close: stringField(row.close),
  };
}

function readBusinessHours(value: unknown): Record<DayKey, DayHours> {
  const src = isRecord(value) ? value : {};
  const out = {} as Record<DayKey, DayHours>;
  for (const day of DAYS) {
    out[day.key] = readDayHours(src[day.key]);
  }
  return out;
}

function readHolidays(value: unknown): Holiday[] {
  if (!Array.isArray(value)) return [];
  return value.map((entry) => {
    const row = isRecord(entry) ? entry : {};
    return {
      date: stringField(row.date),
      label: stringField(row.label),
      closed: row.closed !== false, // schema default true
    };
  });
}

function readServiceType(value: unknown): ServiceType | '' {
  if (typeof value !== 'string') return '';
  return VALID_SERVICE_TYPES.has(value as ServiceType)
    ? (value as ServiceType)
    : '';
}

function readRoles(value: unknown): RoleSlug[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<RoleSlug>();
  for (const item of value) {
    if (typeof item === 'string' && VALID_ROLES.has(item as RoleSlug)) {
      seen.add(item as RoleSlug);
    }
  }
  // Preserve canonical order so toggle sequences don't shuffle the payload.
  return ROLE_OPTIONS.map((o) => o.value).filter((v) => seen.has(v));
}

function readServiceRow(value: unknown): ServiceRow {
  const row = isRecord(value) ? value : {};
  const cents = typeof row.priceCents === 'number' && Number.isFinite(row.priceCents)
    ? row.priceCents
    : null;
  const duration = typeof row.durationMinutes === 'number' && Number.isFinite(row.durationMinutes)
    ? String(row.durationMinutes)
    : '';
  return {
    slug: stringField(row.slug),
    displayName: stringField(row.displayName),
    type: readServiceType(row.type),
    priceDollars: cents === null ? '' : (cents / 100).toFixed(2),
    durationMinutes: duration,
    applicableStaffRoles: readRoles(row.applicableStaffRoles),
    minLeadTimeMinutes: numericString(row.minLeadTimeMinutes) || '0',
    maxAdvanceDays: numericString(row.maxAdvanceDays) || '90',
  };
}

function readServices(value: unknown): ServiceRow[] {
  if (!Array.isArray(value)) return [];
  return value.map(readServiceRow);
}

function readCapacity(value: unknown): CapacityDraft {
  const row = isRecord(value) ? value : {};
  return {
    dailyDaycareCap: numericString(row.dailyDaycareCap),
    overnightBoardingCap: numericString(row.overnightBoardingCap),
    concurrentGroomingSlots: numericString(row.concurrentGroomingSlots),
    concurrentWalksPerWalker:
      numericString(row.concurrentWalksPerWalker) || '3',
  };
}

function readTerminology(value: unknown): Array<[string, string]> {
  if (!isRecord(value)) return [];
  return Object.entries(value).map(([k, v]): [string, string] => [
    k,
    typeof v === 'string' ? v : '',
  ]);
}

function readStep7(draft: Record<string, unknown>): Step7Draft {
  return {
    businessHours: readBusinessHours(draft.businessHours),
    holidays: readHolidays(draft.holidays),
    services: readServices(draft.services),
    capacity: readCapacity(draft.capacityRules),
    terminology: readTerminology(draft.terminologyOverrides),
  };
}

function serializeDayHours(hours: DayHours): Record<string, unknown> {
  const out: Record<string, unknown> = { isOpen: hours.isOpen };
  if (hours.isOpen) {
    if (hours.open !== '') out.open = hours.open;
    if (hours.close !== '') out.close = hours.close;
  }
  return out;
}

function serializeBusinessHours(
  hours: Record<DayKey, DayHours>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const day of DAYS) {
    out[day.key] = serializeDayHours(hours[day.key]);
  }
  return out;
}

function serializeHoliday(h: Holiday): Record<string, unknown> {
  return { date: h.date, label: h.label, closed: h.closed };
}

function parsePriceDollars(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const num = Number(trimmed);
  if (!Number.isFinite(num) || num < 0) return null;
  return Math.round(num * 100);
}

function parseIntField(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const num = Number(trimmed);
  if (!Number.isFinite(num) || !Number.isInteger(num)) return null;
  return num;
}

function serializeServiceRow(row: ServiceRow): Record<string, unknown> {
  const cents = parsePriceDollars(row.priceDollars);
  const duration = parseIntField(row.durationMinutes);
  const minLead = parseIntField(row.minLeadTimeMinutes);
  const maxAdvance = parseIntField(row.maxAdvanceDays);
  const out: Record<string, unknown> = {
    slug: row.slug,
    displayName: row.displayName,
    priceCents: cents === null ? 0 : cents,
    // schema is `.nullable()`: emit null for "no fixed duration".
    durationMinutes: duration,
    applicableStaffRoles: row.applicableStaffRoles,
    minLeadTimeMinutes: minLead === null ? 0 : minLead,
    maxAdvanceDays: maxAdvance === null ? 90 : maxAdvance,
  };
  if (row.type !== '') out.type = row.type;
  return out;
}

function serializeCapacity(c: CapacityDraft): Record<string, unknown> {
  const walks = parseIntField(c.concurrentWalksPerWalker);
  const out: Record<string, unknown> = {
    concurrentWalksPerWalker: walks === null ? 3 : walks,
  };
  const daily = parseIntField(c.dailyDaycareCap);
  if (daily !== null) out.dailyDaycareCap = daily;
  const boarding = parseIntField(c.overnightBoardingCap);
  if (boarding !== null) out.overnightBoardingCap = boarding;
  const grooming = parseIntField(c.concurrentGroomingSlots);
  if (grooming !== null) out.concurrentGroomingSlots = grooming;
  return out;
}

function serializeTerminology(
  rows: ReadonlyArray<[string, string]>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of rows) {
    if (k.trim() === '') continue;
    out[k.trim()] = v;
  }
  return out;
}

function serializeStep7(next: Step7Draft): Record<string, unknown> {
  return {
    businessHours: serializeBusinessHours(next.businessHours),
    holidays: next.holidays.map(serializeHoliday),
    services: next.services.map(serializeServiceRow),
    capacityRules: serializeCapacity(next.capacity),
    terminologyOverrides: serializeTerminology(next.terminology),
  };
}

function groupIssuesByPath(
  issues: ReadonlyArray<{ path: string; message: string }>,
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const issue of issues) {
    const key = issue.path === '' ? '_root' : issue.path;
    const list = out[key] ?? [];
    list.push(issue.message);
    out[key] = list;
  }
  return out;
}

function fieldErrorId(field: string): string {
  return `intake-step7-${field}-error`.replace(/[^a-z0-9-]/gi, '-');
}

function describeHoursError(hours: DayHours): string | null {
  if (!hours.isOpen) return null;
  if (hours.open === '' || hours.close === '') {
    return 'Set both open and close times.';
  }
  if (!HHMM_REGEX.test(hours.open) || !HHMM_REGEX.test(hours.close)) {
    return 'Use HH:MM (24-hour) format.';
  }
  if (hours.close <= hours.open) {
    return 'Close must be after open.';
  }
  return null;
}

function describeSlugError(value: string): string | null {
  if (value.trim() === '') return null;
  if (value.length < 2) return 'Use at least 2 characters.';
  if (value.length > 40) return 'Keep slug to 40 characters.';
  if (!SERVICE_SLUG_REGEX.test(value)) {
    return 'Use lowercase letters, numbers, and dashes only.';
  }
  return null;
}

function describePriceError(value: string): string | null {
  if (value.trim() === '') return null;
  const num = Number(value.trim());
  if (!Number.isFinite(num) || num < 0) return 'Enter a non-negative number.';
  return null;
}

function describeDateError(value: string): string | null {
  if (value.trim() === '') return null;
  if (!ISO_DATE_REGEX.test(value)) return 'Use YYYY-MM-DD format.';
  return null;
}

function detectDuplicateSlugIndices(
  rows: ReadonlyArray<ServiceRow>,
): Set<number> {
  const bySlug = new Map<string, number[]>();
  rows.forEach((row, idx) => {
    const slug = row.slug.trim().toLowerCase();
    if (slug === '') return;
    const list = bySlug.get(slug) ?? [];
    list.push(idx);
    bySlug.set(slug, list);
  });
  const dupes = new Set<number>();
  for (const list of bySlug.values()) {
    if (list.length > 1) list.forEach((idx) => dupes.add(idx));
  }
  return dupes;
}

function makeBlankService(): ServiceRow {
  return {
    slug: '',
    displayName: '',
    type: '',
    priceDollars: '',
    durationMinutes: '',
    applicableStaffRoles: [],
    minLeadTimeMinutes: '0',
    maxAdvanceDays: '90',
  };
}

function makeStarterService(): ServiceRow {
  return {
    slug: 'daycare',
    displayName: 'Daycare',
    type: 'daycare',
    priceDollars: '',
    durationMinutes: '',
    applicableStaffRoles: [],
    minLeadTimeMinutes: '0',
    maxAdvanceDays: '90',
  };
}

function makeBlankHoliday(): Holiday {
  return { date: '', label: '', closed: true };
}

function makeBlankBusinessHours(): Record<DayKey, DayHours> {
  const out = {} as Record<DayKey, DayHours>;
  for (const day of DAYS) {
    out[day.key] = { isOpen: false, open: '', close: '' };
  }
  return out;
}

export type Step7ServiceCatalogProps = IntakeShellRenderProps;

export function Step7ServiceCatalog({
  stepDraft,
  onChange,
  issues,
}: Step7ServiceCatalogProps): JSX.Element {
  const errorsByPath = useMemo(() => groupIssuesByPath(issues), [issues]);
  const value = useMemo(() => readStep7(stepDraft), [stepDraft]);
  const { businessHours, holidays, services, capacity } = value;

  // Local terminology rows so empty rows don't leak into the draft until
  // the prospect actually types a key. Mirrors Step 2's pattern.
  const [terminologyRows, setTerminologyRows] = useState<Array<[string, string]>>(
    () => readTerminology(stepDraft.terminologyOverrides),
  );

  const serverErrorFor = (path: string): string | null => {
    const list = errorsByPath[path];
    return list && list.length > 0 ? list.join(' · ') : null;
  };

  // Mount defaults: seed all 7 days as closed + one starter service row +
  // the schema default for concurrentWalksPerWalker. Skips when any of
  // the three keys is already on the draft so we never clobber a prior
  // session's edits.
  useEffect(() => {
    const needsHours = !isRecord(stepDraft.businessHours);
    const needsServices =
      !Array.isArray(stepDraft.services) || stepDraft.services.length === 0;
    const needsCapacity = !isRecord(stepDraft.capacityRules);
    if (!needsHours && !needsServices && !needsCapacity) return;
    const next: Step7Draft = {
      businessHours: needsHours ? makeBlankBusinessHours() : businessHours,
      holidays,
      services: needsServices ? [makeStarterService()] : services,
      capacity: needsCapacity
        ? {
            dailyDaycareCap: '',
            overnightBoardingCap: '',
            concurrentGroomingSlots: '',
            concurrentWalksPerWalker: '3',
          }
        : capacity,
      terminology: terminologyRows,
    };
    onChange(serializeStep7(next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const commit = (next: Step7Draft): void => {
    onChange(serializeStep7(next));
  };

  const patchDay = (day: DayKey, patch: Partial<DayHours>): void => {
    const nextHours = {
      ...businessHours,
      [day]: { ...businessHours[day], ...patch },
    };
    commit({
      businessHours: nextHours,
      holidays,
      services,
      capacity,
      terminology: terminologyRows,
    });
  };

  const onToggleDayOpen = (day: DayKey, isOpen: boolean): void => {
    // Closing a day clears its open/close so the serialized payload
    // doesn't carry stale times that the schema's refine would reject.
    if (!isOpen) {
      patchDay(day, { isOpen: false, open: '', close: '' });
    } else {
      patchDay(day, { isOpen: true });
    }
  };

  const patchService = (idx: number, patch: Partial<ServiceRow>): void => {
    const nextServices = services.map((row, i) =>
      i === idx ? { ...row, ...patch } : row,
    );
    commit({
      businessHours,
      holidays,
      services: nextServices,
      capacity,
      terminology: terminologyRows,
    });
  };

  const toggleServiceRole = (idx: number, role: RoleSlug): void => {
    const row = services[idx];
    if (!row) return;
    const has = row.applicableStaffRoles.includes(role);
    const nextRoles = has
      ? row.applicableStaffRoles.filter((r) => r !== role)
      : [...row.applicableStaffRoles, role];
    // Re-canonicalize order so toggle sequence doesn't shuffle the payload.
    const canonical = ROLE_OPTIONS.map((o) => o.value).filter((v) =>
      nextRoles.includes(v),
    );
    patchService(idx, { applicableStaffRoles: canonical });
  };

  const addService = (): void => {
    if (services.length >= MAX_SERVICE_ROWS) return;
    commit({
      businessHours,
      holidays,
      services: [...services, makeBlankService()],
      capacity,
      terminology: terminologyRows,
    });
  };

  const removeService = (idx: number): void => {
    if (services.length <= 1) return;
    commit({
      businessHours,
      holidays,
      services: services.filter((_, i) => i !== idx),
      capacity,
      terminology: terminologyRows,
    });
  };

  const addHoliday = (): void => {
    commit({
      businessHours,
      holidays: [...holidays, makeBlankHoliday()],
      services,
      capacity,
      terminology: terminologyRows,
    });
  };

  const patchHoliday = (idx: number, patch: Partial<Holiday>): void => {
    const next = holidays.map((h, i) => (i === idx ? { ...h, ...patch } : h));
    commit({
      businessHours,
      holidays: next,
      services,
      capacity,
      terminology: terminologyRows,
    });
  };

  const removeHoliday = (idx: number): void => {
    commit({
      businessHours,
      holidays: holidays.filter((_, i) => i !== idx),
      services,
      capacity,
      terminology: terminologyRows,
    });
  };

  const patchCapacity = (patch: Partial<CapacityDraft>): void => {
    commit({
      businessHours,
      holidays,
      services,
      capacity: { ...capacity, ...patch },
      terminology: terminologyRows,
    });
  };

  const commitTerminology = (rows: Array<[string, string]>): void => {
    setTerminologyRows(rows);
    commit({
      businessHours,
      holidays,
      services,
      capacity,
      terminology: rows,
    });
  };

  const addTerminologyRow = (): void => {
    if (terminologyRows.length >= MAX_TERMINOLOGY_ROWS) return;
    commitTerminology([
      ...terminologyRows,
      ['', ''] as [string, string],
    ]);
  };

  const patchTerminologyRow = (
    idx: number,
    next: [string, string],
  ): void => {
    const rows = terminologyRows.map((entry, i) => (i === idx ? next : entry));
    commitTerminology(rows);
  };

  const removeTerminologyRow = (idx: number): void => {
    commitTerminology(terminologyRows.filter((_, i) => i !== idx));
  };

  const duplicateSlugs = useMemo(
    () => detectDuplicateSlugIndices(services),
    [services],
  );

  const servicesRootError = serverErrorFor('services');
  const businessHoursRootError = serverErrorFor('businessHours');
  const capacityRootError = serverErrorFor('capacityRules');
  const terminologyRootError = serverErrorFor('terminologyOverrides');

  return (
    <form
      className="ferro-form ferro-intake__form"
      aria-label="Step 7 — Service catalog seed"
      noValidate
      onSubmit={(e) => e.preventDefault()}
    >
      <p className="ferro-muted">
        Tell us when you're open and what services you sell. We'll seed your
        booking calendar from this — you can add and edit services anytime
        after launch.
      </p>

      {/* ── Business hours ─────────────────────────────────── */}
      <section
        className="ferro-field"
        aria-labelledby="intake-step7-hours-heading"
        data-testid="intake-step7-business-hours"
      >
        <h2
          id="intake-step7-hours-heading"
          className="ferro-field__label"
        >
          Business hours
        </h2>
        <p className="ferro-muted">
          Toggle each day you're open and set the time you start and stop
          accepting customers.
        </p>
        {businessHoursRootError ? (
          <span
            role="alert"
            className="ferro-form__error"
            data-testid="intake-step7-business-hours-servererror"
          >
            {businessHoursRootError}
          </span>
        ) : null}
        <ul className="ferro-intake__hoursList">
          {DAYS.map((day) => {
            const hours = businessHours[day.key];
            const clientError = describeHoursError(hours);
            const dayServerError =
              serverErrorFor(`businessHours.${day.key}`) ?? null;
            return (
              <li
                key={day.key}
                className="ferro-intake__hoursRow"
                data-testid={`intake-step7-day-${day.key}`}
              >
                <label className="ferro-field ferro-intake__checkbox">
                  <input
                    type="checkbox"
                    checked={hours.isOpen}
                    onChange={(e) =>
                      onToggleDayOpen(day.key, e.target.checked)
                    }
                    data-testid={`intake-step7-day-${day.key}-open`}
                  />
                  <span className="ferro-field__label">{day.label}</span>
                </label>
                {hours.isOpen ? (
                  <div className="ferro-intake__hoursTimes">
                    <label className="ferro-field">
                      <span className="ferro-field__label">Open</span>
                      <input
                        type="time"
                        className="ferro-field__input"
                        value={hours.open}
                        onChange={(e) =>
                          patchDay(day.key, { open: e.target.value })
                        }
                        aria-invalid={
                          clientError || dayServerError ? 'true' : undefined
                        }
                        data-testid={`intake-step7-day-${day.key}-open-time`}
                      />
                    </label>
                    <label className="ferro-field">
                      <span className="ferro-field__label">Close</span>
                      <input
                        type="time"
                        className="ferro-field__input"
                        value={hours.close}
                        onChange={(e) =>
                          patchDay(day.key, { close: e.target.value })
                        }
                        aria-invalid={
                          clientError || dayServerError ? 'true' : undefined
                        }
                        data-testid={`intake-step7-day-${day.key}-close-time`}
                      />
                    </label>
                  </div>
                ) : null}
                {dayServerError ? (
                  <span
                    role="alert"
                    className="ferro-form__error"
                    data-testid={`intake-step7-day-${day.key}-servererror`}
                  >
                    {dayServerError}
                  </span>
                ) : clientError ? (
                  <span
                    role="alert"
                    className="ferro-form__error"
                    data-testid={`intake-step7-day-${day.key}-error`}
                  >
                    {clientError}
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      </section>

      {/* ── Holidays ───────────────────────────────────────── */}
      <section
        className="ferro-field"
        aria-labelledby="intake-step7-holidays-heading"
      >
        <h2
          id="intake-step7-holidays-heading"
          className="ferro-field__label"
        >
          Holidays (optional)
        </h2>
        <p className="ferro-muted">
          List dates you'll be closed or have special hours. You can edit
          these any time after launch.
        </p>
        <ul
          className="ferro-intake__holidaysList"
          data-testid="intake-step7-holidays-list"
        >
          {holidays.map((h, idx) => {
            const dateError = describeDateError(h.date);
            const dateServerError = serverErrorFor(`holidays.${idx}.date`);
            const labelServerError = serverErrorFor(`holidays.${idx}.label`);
            return (
              <li
                key={idx}
                className="ferro-intake__holidayRow"
                data-testid={`intake-step7-holiday-${idx}`}
              >
                <label className="ferro-field">
                  <span className="ferro-field__label">Date</span>
                  <input
                    type="date"
                    className="ferro-field__input"
                    value={h.date}
                    aria-invalid={
                      dateError || dateServerError ? 'true' : undefined
                    }
                    aria-describedby={
                      dateError || dateServerError
                        ? fieldErrorId(`holiday-${idx}-date`)
                        : undefined
                    }
                    onChange={(e) =>
                      patchHoliday(idx, { date: e.target.value })
                    }
                    data-testid={`intake-step7-holiday-${idx}-date`}
                  />
                  {dateServerError ? (
                    <span
                      id={fieldErrorId(`holiday-${idx}-date`)}
                      role="alert"
                      className="ferro-form__error"
                    >
                      {dateServerError}
                    </span>
                  ) : dateError ? (
                    <span
                      id={fieldErrorId(`holiday-${idx}-date`)}
                      role="alert"
                      className="ferro-form__error"
                    >
                      {dateError}
                    </span>
                  ) : null}
                </label>
                <label className="ferro-field">
                  <span className="ferro-field__label">Label</span>
                  <input
                    type="text"
                    className="ferro-field__input"
                    value={h.label}
                    maxLength={60}
                    aria-invalid={labelServerError ? 'true' : undefined}
                    onChange={(e) =>
                      patchHoliday(idx, { label: e.target.value })
                    }
                    data-testid={`intake-step7-holiday-${idx}-label`}
                  />
                  {labelServerError ? (
                    <span role="alert" className="ferro-form__error">
                      {labelServerError}
                    </span>
                  ) : null}
                </label>
                <label className="ferro-field ferro-intake__checkbox">
                  <input
                    type="checkbox"
                    checked={h.closed}
                    onChange={(e) =>
                      patchHoliday(idx, { closed: e.target.checked })
                    }
                    data-testid={`intake-step7-holiday-${idx}-closed`}
                  />
                  <span>Closed all day</span>
                </label>
                <button
                  type="button"
                  className="ferro-btn ferro-btn--ghost"
                  onClick={() => removeHoliday(idx)}
                  aria-label={`Remove holiday ${idx + 1}`}
                  data-testid={`intake-step7-holiday-${idx}-remove`}
                >
                  Remove
                </button>
              </li>
            );
          })}
        </ul>
        <button
          type="button"
          className="ferro-btn ferro-btn--secondary"
          onClick={addHoliday}
          data-testid="intake-step7-add-holiday"
        >
          Add holiday
        </button>
      </section>

      {/* ── Services ───────────────────────────────────────── */}
      <section
        className="ferro-field"
        aria-labelledby="intake-step7-services-heading"
      >
        <h2
          id="intake-step7-services-heading"
          className="ferro-field__label"
        >
          Services
        </h2>
        <p className="ferro-muted">
          Add the services you offer. At least one is required. Slug is the
          internal identifier — keep it short and lowercase.
        </p>
        {servicesRootError ? (
          <span
            role="alert"
            className="ferro-form__error"
            data-testid="intake-step7-services-servererror"
          >
            {servicesRootError}
          </span>
        ) : null}
        <ul
          className="ferro-intake__servicesList"
          data-testid="intake-step7-services-list"
        >
          {services.map((row, idx) => {
            const slugClientError = describeSlugError(row.slug);
            const priceClientError = describePriceError(row.priceDollars);
            const isDuplicate = duplicateSlugs.has(idx);
            const slugServerError = serverErrorFor(`services.${idx}.slug`);
            const displayServerError = serverErrorFor(
              `services.${idx}.displayName`,
            );
            const typeServerError = serverErrorFor(`services.${idx}.type`);
            const priceServerError = serverErrorFor(
              `services.${idx}.priceCents`,
            );
            const durationServerError = serverErrorFor(
              `services.${idx}.durationMinutes`,
            );
            return (
              <li
                key={idx}
                className="ferro-intake__serviceRow"
                data-testid={`intake-step7-service-${idx}`}
              >
                <div className="ferro-intake__serviceRow-grid">
                  <label className="ferro-field">
                    <span className="ferro-field__label">Slug</span>
                    <input
                      type="text"
                      className="ferro-field__input"
                      value={row.slug}
                      autoComplete="off"
                      spellCheck={false}
                      aria-invalid={
                        slugServerError || slugClientError || isDuplicate
                          ? 'true'
                          : undefined
                      }
                      aria-describedby={
                        slugServerError || slugClientError || isDuplicate
                          ? fieldErrorId(`service-${idx}-slug`)
                          : undefined
                      }
                      onChange={(e) =>
                        patchService(idx, { slug: e.target.value })
                      }
                      data-testid={`intake-step7-service-${idx}-slug`}
                    />
                    {slugServerError ? (
                      <span
                        id={fieldErrorId(`service-${idx}-slug`)}
                        role="alert"
                        className="ferro-form__error"
                      >
                        {slugServerError}
                      </span>
                    ) : isDuplicate ? (
                      <span
                        id={fieldErrorId(`service-${idx}-slug`)}
                        role="alert"
                        className="ferro-form__error"
                        data-testid={`intake-step7-service-${idx}-slug-duplicate`}
                      >
                        This slug is already on another row.
                      </span>
                    ) : slugClientError ? (
                      <span
                        id={fieldErrorId(`service-${idx}-slug`)}
                        role="alert"
                        className="ferro-form__error"
                      >
                        {slugClientError}
                      </span>
                    ) : null}
                  </label>

                  <label className="ferro-field">
                    <span className="ferro-field__label">Display name</span>
                    <input
                      type="text"
                      className="ferro-field__input"
                      value={row.displayName}
                      maxLength={80}
                      aria-invalid={displayServerError ? 'true' : undefined}
                      onChange={(e) =>
                        patchService(idx, { displayName: e.target.value })
                      }
                      data-testid={`intake-step7-service-${idx}-displayName`}
                    />
                    {displayServerError ? (
                      <span role="alert" className="ferro-form__error">
                        {displayServerError}
                      </span>
                    ) : null}
                  </label>

                  <label className="ferro-field">
                    <span className="ferro-field__label">Type</span>
                    <select
                      className="ferro-field__input"
                      value={row.type}
                      aria-invalid={typeServerError ? 'true' : undefined}
                      onChange={(e) =>
                        patchService(idx, {
                          type: readServiceType(e.target.value),
                        })
                      }
                      data-testid={`intake-step7-service-${idx}-type`}
                    >
                      <option value="">Select a type…</option>
                      {SERVICE_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                    {typeServerError ? (
                      <span role="alert" className="ferro-form__error">
                        {typeServerError}
                      </span>
                    ) : null}
                  </label>

                  <label className="ferro-field">
                    <span className="ferro-field__label">
                      Price (USD)
                    </span>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="ferro-field__input"
                      value={row.priceDollars}
                      placeholder="0.00"
                      aria-invalid={
                        priceServerError || priceClientError ? 'true' : undefined
                      }
                      aria-describedby={
                        priceServerError || priceClientError
                          ? fieldErrorId(`service-${idx}-price`)
                          : undefined
                      }
                      onChange={(e) =>
                        patchService(idx, { priceDollars: e.target.value })
                      }
                      data-testid={`intake-step7-service-${idx}-price`}
                    />
                    {priceServerError ? (
                      <span
                        id={fieldErrorId(`service-${idx}-price`)}
                        role="alert"
                        className="ferro-form__error"
                      >
                        {priceServerError}
                      </span>
                    ) : priceClientError ? (
                      <span
                        id={fieldErrorId(`service-${idx}-price`)}
                        role="alert"
                        className="ferro-form__error"
                      >
                        {priceClientError}
                      </span>
                    ) : null}
                  </label>

                  <label className="ferro-field">
                    <span className="ferro-field__label">
                      Duration (minutes, optional)
                    </span>
                    <input
                      type="number"
                      min={1}
                      className="ferro-field__input"
                      value={row.durationMinutes}
                      aria-invalid={durationServerError ? 'true' : undefined}
                      onChange={(e) =>
                        patchService(idx, { durationMinutes: e.target.value })
                      }
                      data-testid={`intake-step7-service-${idx}-duration`}
                    />
                    {durationServerError ? (
                      <span role="alert" className="ferro-form__error">
                        {durationServerError}
                      </span>
                    ) : null}
                  </label>

                  <label className="ferro-field">
                    <span className="ferro-field__label">
                      Min lead time (minutes)
                    </span>
                    <input
                      type="number"
                      min={0}
                      className="ferro-field__input"
                      value={row.minLeadTimeMinutes}
                      onChange={(e) =>
                        patchService(idx, {
                          minLeadTimeMinutes: e.target.value,
                        })
                      }
                      data-testid={`intake-step7-service-${idx}-minLead`}
                    />
                  </label>

                  <label className="ferro-field">
                    <span className="ferro-field__label">
                      Max booking horizon (days)
                    </span>
                    <input
                      type="number"
                      min={1}
                      className="ferro-field__input"
                      value={row.maxAdvanceDays}
                      onChange={(e) =>
                        patchService(idx, { maxAdvanceDays: e.target.value })
                      }
                      data-testid={`intake-step7-service-${idx}-maxAdvance`}
                    />
                  </label>

                  <fieldset className="ferro-field">
                    <legend className="ferro-field__label">
                      Staff roles allowed (optional)
                    </legend>
                    <div className="ferro-intake__roleGrid">
                      {ROLE_OPTIONS.map((opt) => (
                        <label
                          key={opt.value}
                          className="ferro-field ferro-intake__checkbox"
                        >
                          <input
                            type="checkbox"
                            checked={row.applicableStaffRoles.includes(
                              opt.value,
                            )}
                            onChange={() => toggleServiceRole(idx, opt.value)}
                            data-testid={`intake-step7-service-${idx}-role-${opt.value}`}
                          />
                          <span>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>

                </div>

                <div className="ferro-intake__serviceRow-actions">
                  <button
                    type="button"
                    className="ferro-btn ferro-btn--ghost"
                    onClick={() => removeService(idx)}
                    disabled={services.length <= 1}
                    aria-label={`Remove service ${idx + 1}`}
                    data-testid={`intake-step7-service-${idx}-remove`}
                  >
                    Remove
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
        <button
          type="button"
          className="ferro-btn ferro-btn--secondary"
          onClick={addService}
          disabled={services.length >= MAX_SERVICE_ROWS}
          data-testid="intake-step7-add-service"
        >
          Add service
        </button>
      </section>

      {/* ── Capacity rules ─────────────────────────────────── */}
      <section
        className="ferro-field"
        aria-labelledby="intake-step7-capacity-heading"
      >
        <h2
          id="intake-step7-capacity-heading"
          className="ferro-field__label"
        >
          Capacity rules
        </h2>
        <p className="ferro-muted">
          Optional caps on concurrent and daily bookings. Leave a field
          blank if you don't enforce that cap.
        </p>
        {capacityRootError ? (
          <span
            role="alert"
            className="ferro-form__error"
            data-testid="intake-step7-capacity-servererror"
          >
            {capacityRootError}
          </span>
        ) : null}
        <div className="ferro-intake__capacityGrid">
          <label className="ferro-field">
            <span className="ferro-field__label">
              Daily daycare cap (optional)
            </span>
            <input
              type="number"
              min={1}
              className="ferro-field__input"
              value={capacity.dailyDaycareCap}
              onChange={(e) =>
                patchCapacity({ dailyDaycareCap: e.target.value })
              }
              data-testid="intake-step7-capacity-dailyDaycareCap"
            />
          </label>
          <label className="ferro-field">
            <span className="ferro-field__label">
              Overnight boarding cap (optional)
            </span>
            <input
              type="number"
              min={1}
              className="ferro-field__input"
              value={capacity.overnightBoardingCap}
              onChange={(e) =>
                patchCapacity({ overnightBoardingCap: e.target.value })
              }
              data-testid="intake-step7-capacity-overnightBoardingCap"
            />
          </label>
          <label className="ferro-field">
            <span className="ferro-field__label">
              Concurrent grooming slots (optional)
            </span>
            <input
              type="number"
              min={1}
              className="ferro-field__input"
              value={capacity.concurrentGroomingSlots}
              onChange={(e) =>
                patchCapacity({ concurrentGroomingSlots: e.target.value })
              }
              data-testid="intake-step7-capacity-concurrentGroomingSlots"
            />
          </label>
          <label className="ferro-field">
            <span className="ferro-field__label">
              Concurrent walks per walker
            </span>
            <input
              type="number"
              min={1}
              className="ferro-field__input"
              value={capacity.concurrentWalksPerWalker}
              onChange={(e) =>
                patchCapacity({ concurrentWalksPerWalker: e.target.value })
              }
              data-testid="intake-step7-capacity-concurrentWalksPerWalker"
            />
          </label>
        </div>
      </section>

      {/* ── Terminology overrides ──────────────────────────── */}
      <section
        className="ferro-field"
        aria-labelledby="intake-step7-terminology-heading"
      >
        <h2
          id="intake-step7-terminology-heading"
          className="ferro-field__label"
        >
          Booking terminology (optional)
        </h2>
        <p className="ferro-muted">
          Rename booking-flow concepts to match your brand voice. We'll use
          these labels across the customer and admin apps.
        </p>
        {terminologyRootError ? (
          <span
            role="alert"
            className="ferro-form__error"
            data-testid="intake-step7-terminology-servererror"
          >
            {terminologyRootError}
          </span>
        ) : null}
        <ul data-testid="intake-step7-terminology-list">
          {terminologyRows.map(([key, val], idx) => (
            <li
              key={`terminology-${idx}`}
              className="ferro-intake__terminologyRow"
              data-testid={`intake-step7-terminology-row-${idx}`}
            >
              <label className="ferro-field">
                <span className="ferro-field__label">Concept</span>
                <input
                  type="text"
                  className="ferro-field__input"
                  value={key}
                  placeholder="reservation"
                  onChange={(e) =>
                    patchTerminologyRow(idx, [e.target.value, val])
                  }
                  data-testid={`intake-step7-terminology-key-${idx}`}
                />
              </label>
              <label className="ferro-field">
                <span className="ferro-field__label">Your label</span>
                <input
                  type="text"
                  className="ferro-field__input"
                  value={val}
                  placeholder="visit"
                  onChange={(e) =>
                    patchTerminologyRow(idx, [key, e.target.value])
                  }
                  data-testid={`intake-step7-terminology-value-${idx}`}
                />
              </label>
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost"
                onClick={() => removeTerminologyRow(idx)}
                aria-label={`Remove terminology row ${idx + 1}`}
                data-testid={`intake-step7-terminology-remove-${idx}`}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
        <button
          type="button"
          className="ferro-btn ferro-btn--secondary"
          onClick={addTerminologyRow}
          disabled={terminologyRows.length >= MAX_TERMINOLOGY_ROWS}
          data-testid="intake-step7-add-terminology"
        >
          Add terminology override
        </button>
      </section>
    </form>
  );
}
